import React, { useState } from "react";
import styled from "styled-components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import PageHeader from "../common/PageHeader";
import { ShowAllContainer } from "../common/ShowAllContainer";
import Button from "../common/Button";
import data from "../../data.json";

const HonorsWrapper = styled.section`
  ${(props) => props.theme.spacing.sectionBottom};
`;

const HonorsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 40px;
  background-color: ${(props) => props.theme.secondaryColor};
  box-shadow: ${(props) => props.theme.shadowSmall};
  border-radius: 10px;
  @media ${(props) => props.theme.media.tablet} {
    padding: 40px 30px;
  }
  @media ${(props) => props.theme.media.mobile} {
    padding: 25px 15px;
  }
`;

const HonorCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding-bottom: 20px;
  border-bottom: 1px solid
    ${(props) =>
        props.theme.dark ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.1)"};

  &:last-child {
    border-bottom: none;
    padding-bottom: 0;
  }

  h3 {
    margin: 0;
    color: ${(props) => props.theme.primaryColor};
    font-size: 18px;
    font-weight: 600;
  }

  p {
    margin: 0;
    font-size: 14px;
    color: ${(props) => props.theme.primaryText};
  }
`;

const HonorDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const IssuedBy = styled.p`
  color: ${(props) => props.theme.primaryText};
  font-size: 14px;
`;

const AssociatedWith = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  .logo {
    width: 20px;
    height: 20px;
    border-radius: 4px;
    object-fit: contain;
  }

  .icon {
    width: 20px;
    height: 20px;
    border-radius: 4px;
    background-color: ${(props) => props.theme.primaryColor};
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-weight: bold;
    font-size: 12px;
  }

  p {
    font-size: 14px;
    color: ${(props) => props.theme.primaryText};
  }
`;

const ThumbnailContainer = styled.div`
  margin-top: 12px;
  display: flex;
  align-items: center;
  gap: 12px;

  a {
    cursor: pointer;
    text-decoration: none;
  }

  img {
    width: 105px;
    height: 60px;
    border-radius: 6px;
    object-fit: cover;
    border: 1px solid
      ${(props) =>
        props.theme.dark ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.1)"};
    transition: opacity 0.2s;

    &:hover {
      opacity: 0.8;
    }
  }

  .thumbnail-label {
    font-size: 14px;
    color: ${(props) => props.theme.primaryText};
    font-weight: 500;
  }
`;

const Honors = () => {
    const honors = data.honors || [];
    const companies = data.companies || [];
    const [showAll, setShowAll] = useState(honors.length > 2 ? false : true);

    const handleShowAll = () => {
        setShowAll(true);
    };

    return (
        <HonorsWrapper id="honors">
            <PageHeader>Honors & Awards</PageHeader>
            <ShowAllContainer collapseHeight="400px" showAll={showAll}>
                <HonorsContainer>
                    {honors.map((honor) => {
                        const company = companies.find((c) => c.id === honor.company_id);
                        const companyName = company ? company.name : "Unknown";
                        const companyLogo = company ? company.logo : null;

                        return (
                            <HonorCard key={honor.id}>
                                <h3>{honor.title}</h3>
                                <HonorDetails>
                                    <IssuedBy>
                                        Issued by {companyName} · {honor.issue_date}
                                    </IssuedBy>
                                    <AssociatedWith>
                                        {companyLogo ? (
                                            <img src={companyLogo} alt={companyName} className="logo" />
                                        ) : (
                                            <div className="icon">
                                                {companyName ? companyName.charAt(0).toUpperCase() : "A"}
                                            </div>
                                        )}
                                        <p>Associated with {companyName}</p>
                                    </AssociatedWith>
                                    {honor.thumbnail && (
                                        <ThumbnailContainer>
                                            <a
                                                href={honor.thumbnail}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                <img src={honor.thumbnail} alt={honor.title} />
                                            </a>
                                            <span className="thumbnail-label">{honor.title}</span>
                                        </ThumbnailContainer>
                                    )}
                                </HonorDetails>
                            </HonorCard>
                        );
                    })}
                </HonorsContainer>
                {!showAll && (
                    <Button onClick={handleShowAll} className="showall__button">
                        Show all {honors.length} honors&nbsp;&nbsp;
                        <FontAwesomeIcon icon="arrow-right-long" />
                    </Button>
                )}
            </ShowAllContainer>
        </HonorsWrapper>
    );
};

export default Honors;

